if (!customElements.get('enhanced-pickup-availability')) {
  class EnhancedPickupAvailability extends HTMLElement {
    constructor() {
      super();

      this.lastFocusedElement = null;
      this.currentVariantId = this.dataset.currentVariant || '';
      this.updateTimer = null;
      this.abortController = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleVariantEvent = this.handleVariantEvent.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('change', this.handleDocumentChange);

      [
        'variant:change',
        'variant:changed',
        'product:variant-change',
        'product:variant-changed'
      ].forEach((eventName) => {
        document.addEventListener(eventName, this.handleVariantEvent);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('change', this.handleDocumentChange);

      [
        'variant:change',
        'variant:changed',
        'product:variant-change',
        'product:variant-changed'
      ].forEach((eventName) => {
        document.removeEventListener(eventName, this.handleVariantEvent);
      });

      this.abortController?.abort();
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-enhanced-pickup-open]');
      const closeButton = event.target.closest('[data-enhanced-pickup-close]');
      const overlay = event.target.closest('[data-enhanced-pickup-overlay]');

      if (openButton) {
        this.openDrawer(openButton);
        return;
      }

      if (closeButton || overlay) {
        this.closeDrawer();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.closeDrawer();
        return;
      }

      if (event.key === 'Tab' && this.drawer?.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    }

    handleDocumentChange(event) {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      const productArea = this.closest(
        '.shopify-section, [data-section-id], section, product-info'
      );

      if (productArea && !productArea.contains(target)) return;

      if (
        target.matches('[name="id"]') ||
        target.matches('select[name*="option"]') ||
        target.matches('input[name*="option"]') ||
        target.closest('variant-selects, variant-radios, product-variants')
      ) {
        this.scheduleVariantRefresh();
      }
    }

    handleVariantEvent(event) {
      const variant =
        event.detail?.variant ||
        event.detail?.selectedVariant ||
        event.detail?.productVariant;

      const variantId = variant?.id || event.detail?.variantId;

      if (variantId) {
        this.refreshForVariant(String(variantId));
        return;
      }

      this.scheduleVariantRefresh();
    }

    scheduleVariantRefresh() {
      window.clearTimeout(this.updateTimer);

      this.updateTimer = window.setTimeout(() => {
        const variantId = this.getSelectedVariantId();

        if (variantId) {
          this.refreshForVariant(variantId);
        }
      }, 120);
    }

    getSelectedVariantId() {
      const productArea = this.closest(
        '.shopify-section, [data-section-id], section, product-info'
      );

      const variantInput =
        productArea?.querySelector(
          'form[action*="/cart/add"] [name="id"]:checked'
        ) ||
        productArea?.querySelector(
          'form[action*="/cart/add"] select[name="id"]'
        ) ||
        productArea?.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        );

      if (variantInput?.value) {
        return String(variantInput.value);
      }

      return new URL(window.location.href).searchParams.get('variant');
    }

    async refreshForVariant(variantId) {
      if (!variantId || variantId === this.currentVariantId) return;

      const productUrl = this.dataset.productUrl;
      const sectionId = this.dataset.sectionId;

      if (!productUrl || !sectionId) return;

      this.abortController?.abort();
      this.abortController = new AbortController();

      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('variant', variantId);
      requestUrl.searchParams.set('section_id', sectionId);

      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: this.abortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Pickup availability request failed: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const documentFragment = parser.parseFromString(html, 'text/html');

        const newContent = documentFragment.querySelector(
          '[data-enhanced-pickup-content]'
        );

        const dynamicContainer = this.querySelector(
          '.enhanced-pickup__dynamic'
        );

        if (!newContent || !dynamicContainer) {
          throw new Error('Pickup availability markup was not returned.');
        }

        this.closeDrawer(false);
        dynamicContainer.innerHTML = newContent.outerHTML;

        this.currentVariantId =
          newContent.dataset.renderedVariant || variantId;

        this.dataset.currentVariant = this.currentVariantId;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Enhanced pickup availability]', error);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    openDrawer(trigger) {
      const drawer = this.drawer;
      const overlay = this.overlay;

      if (!drawer || !overlay) return;

      this.lastFocusedElement = trigger || document.activeElement;

      overlay.hidden = false;

      requestAnimationFrame(() => {
        overlay.classList.add('is-visible');
        drawer.classList.add('is-open');
      });

      drawer.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('enhanced-pickup-drawer-open');

      window.setTimeout(() => {
        drawer.querySelector('[data-enhanced-pickup-close]')?.focus();
      }, 50);
    }

    closeDrawer(restoreFocus = true) {
      const drawer = this.drawer;
      const overlay = this.overlay;

      if (!drawer || !overlay) return;

      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');

      this.querySelectorAll('[data-enhanced-pickup-open]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      document.documentElement.classList.remove(
        'enhanced-pickup-drawer-open'
      );

      window.setTimeout(() => {
        overlay.hidden = true;
      }, 260);

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
        this.lastFocusedElement.focus();
      }
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('hidden'));

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    get drawer() {
      return this.querySelector('.enhanced-pickup__drawer');
    }

    get overlay() {
      return this.querySelector('[data-enhanced-pickup-overlay]');
    }
  }

  customElements.define(
    'enhanced-pickup-availability',
    EnhancedPickupAvailability
  );
}